import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { shouldShowAdminSidebar } from '../../utils/topChromeActions';
import { useSupportAssignments } from '../../pages/Admin/useSupportAssignments';
import { Toaster } from 'react-hot-toast';
import SupportContextBanner from './SupportContextBanner';
import { readSupportedTenantSession, subscribeSupportedTenantSession } from '../../pages/Admin/supportedTenantsSession';
const MainLayout = ({
  children,
  minimalHeader = false,
  hideSupportButton: _hideSupportButton = false,
  /** When true, the main content area scrolls (admin default is overflow-hidden). */
  scrollMain = false,
}) => {
  const { currentUser, isAdmin, role, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const location = useLocation();
  const supportAssignments = useSupportAssignments(currentUser?.uid, dealerAdmin.active);
  const showAdminSidebar = shouldShowAdminSidebar({
    isAdmin,
    role,
    pathname: location.pathname,
    dealerActive: dealerAdmin.active,
    permissions: userData?.permissions,
    supportAccess: supportAssignments.length > 0 || location.pathname.startsWith('/support/tenants'),
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [supportSession, setSupportSession] = useState(() => readSupportedTenantSession());
  useEffect(() => subscribeSupportedTenantSession(() => setSupportSession(readSupportedTenantSession())), []);

  const adminMainOverflow = scrollMain ? 'overflow-auto' : 'overflow-hidden';

  return (
    // Lock shell to the viewport so body never gets a right-edge page scrollbar.
    // Page content scrolls inside <main> or inner cards (e.g. Flows / Agent Management).
    <div
      className="h-screen max-h-screen overflow-hidden bg-gray-100 flex flex-col"
      style={{ paddingRight: 'var(--sb-assistant-dock-width, 0px)' }}
    >
      {/* Toast notifications container */}
      <Toaster position="top-right" />

      {/* Hamburger for mobile - only for admins */}
      {showAdminSidebar && (
        <button
          className="fixed top-4 left-4 z-40 md:hidden bg-gray-800 text-white p-2 rounded shadow focus:outline-none"
          aria-label="Open sidebar"
          onClick={() => setIsSidebarOpen(true)}
        >
          {/* Hamburger icon */}
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      )}

      {!showAdminSidebar && (
        <div className="flex-shrink-0">
          <Header minimal={minimalHeader} hideHelp={showAdminSidebar} />
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {showAdminSidebar && <Sidebar open={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />}

        {/* Main content */}
        <main
          className={`flex-1 min-h-0 min-w-0 ${supportSession?.accessLevel === 'view' ? 'sb-support-view' : ''} ${showAdminSidebar ? `w-full ${adminMainOverflow}` : 'overflow-auto max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8'}`}
        >
          <SupportContextBanner />
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
